import orderBy from 'lodash/orderBy';
import React from 'react';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';

import { usePaginatedFetch } from '../../../hooks/usePaginatedFetch';
import { UamTitleSummary } from '../../../trustAdmin-api/generated-src';
import TrustAdminApiFactory from '../../../trustAdmin-api/TrustAdminApiFactory';
import { formatTimestamp } from '../../../utils/formatTimestamp';
import { translateToLodashSortDirection } from '../../../utils/sort';
import { RequestErrorCard } from '../error/RequestErrorCard';
import { UamAuditHistory } from '../UamAuditHistory/UamAuditHistory';

type FilterTab = 'all' | 'enabled' | 'disabled';

export type UamColumnId = 'tconst' | 'titleName' | 'enabled' | 'adminAlias' | 'updatedAt' | 'note';

const TAB_TO_ENABLED_PARAM: Record<FilterTab, boolean | undefined> = {
    all: undefined,
    enabled: true,
    disabled: false
};

const TAB_EMPTY_STATE_LABEL: Record<FilterTab, string> = {
    all: '',
    enabled: 'enabled ',
    disabled: 'disabled '
};

const SORTABLE_HEADERS: { id: UamColumnId; label: string; sortable?: boolean }[] = [
    { id: 'tconst', label: 'Tconst', sortable: true },
    { id: 'titleName', label: 'Title Name', sortable: true },
    { id: 'enabled', label: 'Status', sortable: true },
    { id: 'adminAlias', label: 'Admin', sortable: true },
    { id: 'updatedAt', label: 'Last Updated', sortable: true },
    { id: 'note', label: 'Note' }
];

// --- Reducer for coordinated UI state ---

interface UamTableState {
    activeTab: FilterTab;
    searchQuery: string;
    sortColumn: UamColumnId | undefined;
    sortDirection: TableSortDirection | undefined;
    selectedTconst: string | undefined;
}

type UamTableAction =
    | { type: 'RESET_FOR_TAB_CHANGE'; tab: FilterTab }
    | { type: 'SET_SEARCH_QUERY'; query: string }
    | { type: 'SET_SORT'; column: UamColumnId; direction: TableSortDirection }
    | { type: 'TOGGLE_ROW'; tconst: string };

const initialState: UamTableState = {
    activeTab: 'all',
    searchQuery: '',
    sortColumn: undefined,
    sortDirection: undefined,
    selectedTconst: undefined
};

function uamTableReducer(state: UamTableState, action: UamTableAction): UamTableState {
    switch (action.type) {
        case 'RESET_FOR_TAB_CHANGE':
            return {
                ...initialState,
                activeTab: action.tab
            };
        case 'SET_SEARCH_QUERY':
            return { ...state, searchQuery: action.query };
        case 'SET_SORT':
            return { ...state, sortColumn: action.column, sortDirection: action.direction };
        case 'TOGGLE_ROW':
            return {
                ...state,
                selectedTconst: state.selectedTconst === action.tconst ? undefined : action.tconst
            };
        default:
            return state;
    }
}

// --- Component ---

export const UamOverviewTable: React.FC = () => {
    const [state, dispatch] = React.useReducer(uamTableReducer, initialState);
    const { activeTab, searchQuery, sortColumn, sortDirection, selectedTconst } = state;

    const enabledFilter = TAB_TO_ENABLED_PARAM[activeTab];

    const {
        items: titles,
        nextToken,
        isInitialLoading,
        isLoadingMore,
        error,
        loadMore
    } = usePaginatedFetch<UamTitleSummary>(
        (token) => TrustAdminApiFactory().unusualActivity.listUnusualActivity(undefined, token, enabledFilter),
        (data) => data.titles,
        [enabledFilter]
    );

    const handleTabChange = (newTab: FilterTab) => {
        if (newTab === activeTab) return;
        dispatch({ type: 'RESET_FOR_TAB_CHANGE', tab: newTab });
    };

    const handleSort = ({
        sortColumn: col,
        sortDirection: dir
    }: {
        sortColumn: string;
        sortDirection: TableSortDirection;
    }) => {
        dispatch({ type: 'SET_SORT', column: col as UamColumnId, direction: dir });
    };

    const handleRowClick = (tconst: string) => {
        dispatch({ type: 'TOGGLE_ROW', tconst });
    };

    const filteredTitles = searchQuery
        ? titles.filter(
              (t) =>
                  t.tconst.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (t.titleName || '').toLowerCase().includes(searchQuery.toLowerCase())
          )
        : titles;

    const sortedTitles =
        sortColumn && sortDirection
            ? orderBy(filteredTitles, [sortColumn], [translateToLodashSortDirection(sortDirection)])
            : filteredTitles;

    const emptyStateMessage = `No ${TAB_EMPTY_STATE_LABEL[activeTab]}UAM titles found.`;

    // Derive error visibility: hide the error card while a retry is in-flight
    const shouldShowError = error && !isLoadingMore;

    const showError = () => <RequestErrorCard error={error!} />;

    const showTable = () => (
        <>
            <Row alignmentVertical='bottom' widths={['fill', 'fit']}>
                <TabGroup value={activeTab} onChange={handleTabChange} data-testid='uam-tab-group'>
                    <Tab value='all' data-testid='uam-tab-all'>
                        All UAM Titles
                    </Tab>
                    <Tab value='enabled' data-testid='uam-tab-enabled'>
                        Enabled
                    </Tab>
                    <Tab value='disabled' data-testid='uam-tab-disabled'>
                        Disabled
                    </Tab>
                </TabGroup>
                <SearchField
                    value={searchQuery}
                    onChange={(query: string) => dispatch({ type: 'SET_SEARCH_QUERY', query })}
                    onSubmit={() => undefined}
                    placeholder='Search by tconst or title name...'
                    data-testid='uam-search-field'
                />
            </Row>
            {!(isInitialLoading && titles.length === 0) && (
                <Text type='b200' color='secondary' data-testid='uam-record-count'>
                    Showing {sortedTitles.length} of {titles.length} records
                </Text>
            )}
            {isInitialLoading && titles.length === 0 ? (
                <Column alignmentHorizontal='center' spacingInset='500'>
                    <Loader size='large' data-testid='uam-initial-loader' />
                </Column>
            ) : sortedTitles.length === 0 ? (
                <Text data-testid='uam-empty-state'>{emptyStateMessage}</Text>
            ) : (
                <>
                    <Table
                        headerRows={1}
                        showDividers={true}
                        showExpandedDividers={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        data-testid='uam-table'
                    >
                        <TableRow>
                            {SORTABLE_HEADERS.map((h) => (
                                <TableCell key={h.id} sortColumn={h.sortable ? h.id : undefined}>
                                    {h.label}
                                </TableCell>
                            ))}
                        </TableRow>
                        {sortedTitles.map((title) => {
                            const isExpanded = selectedTconst === title.tconst;
                            return (
                                <TableRow
                                    key={title.tconst}
                                    highlightOnHover={true}
                                    open={isExpanded}
                                    onClick={() => handleRowClick(title.tconst)}
                                    data-testid={`uam-row-${title.tconst}`}
                                >
                                    <TableCell>
                                        <Link
                                            href={`https://www.imdb.com/title/${title.tconst}/`}
                                            target='_blank'
                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        >
                                            {title.tconst}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{title.titleName || <Text color='secondary'>—</Text>}</TableCell>
                                    <TableCell>
                                        <Tag type={title.enabled ? 'success' : 'neutral'}>
                                            {title.enabled ? 'Enabled' : 'Disabled'}
                                        </Tag>
                                    </TableCell>
                                    <TableCell>{title.adminAlias}</TableCell>
                                    <TableCell>{formatTimestamp(title.updatedAt)}</TableCell>
                                    <TableCell>
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{title.note}</span>
                                    </TableCell>
                                    <TableRow data-testid={`uam-detail-row-${title.tconst}`}>
                                        <TableCell columnSpan={6}>
                                            {isExpanded && <UamAuditHistory tconst={title.tconst} />}
                                        </TableCell>
                                    </TableRow>
                                </TableRow>
                            );
                        })}
                    </Table>
                    {nextToken && !searchQuery && (
                        <Row alignmentHorizontal='center' spacingInset='300'>
                            <Button
                                type='secondary'
                                onClick={loadMore}
                                disabled={isLoadingMore}
                                data-testid='uam-load-more-button'
                            >
                                {isLoadingMore ? <Loader type='linear' size='small' /> : 'Load More'}
                            </Button>
                        </Row>
                    )}
                    {shouldShowError && titles.length > 0 && <RequestErrorCard error={error} />}
                </>
            )}
        </>
    );

    return (shouldShowError && titles.length === 0 && showError()) || showTable();
};
